import { useCallback, useRef, useState } from "react";
import FilterArea from "./components/FilterArea";
import TableArea from "./components/TableArea";
import { useLazyFetchImportsAndExportsQuery } from "@store/master";

const DATA_PAGE_SIZE = 30;

export default function Manage() {
  const [tableData, setTableData] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DATA_PAGE_SIZE,
    total: 0,
  });
  const lastFiltersRef = useRef({});
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const [fetchImportsAndExportsData, { isLoading, isFetching }] =
    useLazyFetchImportsAndExportsQuery();

  const fetchTableData = useCallback(
    async (filters, page, perPage) => {
      if (!filters.subTypeMasterId) return;
      const payload = {
        subTypeMasterId: filters.subTypeMasterId,
        page,
        perPage,
      };
      if (filters.search) {
        payload.search = filters.search;
      }
      if (filters.fromDate && filters.toDate) {
        payload.fromDate = filters.fromDate;
        payload.toDate = filters.toDate;
      }

      const result = await fetchImportsAndExportsData(payload);

      if (result.data?.success) {
        setTableData(result.data.data);
        setPagination((prev) => ({
          ...prev,
          current: result.data.pagination?.current_page ?? page,
          pageSize: result.data.pagination?.total_perpage ?? perPage,
          total: result.data.pagination?.total_records ?? 0,
        }));
      }
    },
    [fetchImportsAndExportsData],
  );

  const handleFiltersChange = useCallback(
    async (filters) => {
      lastFiltersRef.current = filters;
      setPagination((prev) => ({ ...prev, current: 1 }));
      await fetchTableData(filters, 1, paginationRef.current.pageSize);
    },
    [fetchTableData],
  );

  const handlePaginationChange = useCallback(
    async (newPagination) => {
      setPagination((prev) => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      }));
      await fetchTableData(
        lastFiltersRef.current,
        newPagination.current,
        newPagination.pageSize,
      );
    },
    [fetchTableData],
  );

  const handleCellUpdated = useCallback((id, field, newValue) => {
    setTableData(
      (prev) =>
        prev?.map((row) => {
          if (row.id !== id) return row;

          switch (field) {
            case "Country (Manufacturer)":
              return {
                ...row,
                LeavesCountry: {
                  ...row.LeavesCountry,
                  name: newValue,
                },
              };
            case "Manufacturer":
              return {
                ...row,
                Exporter: {
                  ...row.Exporter,
                  CompanyMaster: {
                    ...row.Exporter?.CompanyMaster,
                    name: newValue,
                  },
                },
              };
            case "Consignee":
              return {
                ...row,
                Importer: {
                  ...row.Importer,
                  CompanyMaster: {
                    ...row.Importer?.CompanyMaster,
                    name: newValue,
                  },
                },
              };
            case "Country (Consignee)":
              return {
                ...row,
                ArrivalCountry: {
                  ...row.ArrivalCountry,
                  name: newValue,
                },
              };
            default:
              return row;
          }
        }) ?? null,
    );
  }, []);

  return (
    <div className="p-2 h-full rounded-lg md:p-5 animate-fadeIn bg-secondary-main">
      <div className="flex flex-col gap-5">
        {/* heading */}
        <div className="flex flex-col pb-1.5 items-start justify-between border-b md:items-center md:flex-row border-b-border">
          <label className="text-lg font-semibold leading-6 text-text-header">
            Manage
          </label>
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <FilterArea onFiltersChange={handleFiltersChange} />
          <TableArea
            tableData={tableData}
            loading={isLoading || isFetching}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            onCellUpdated={handleCellUpdated}
          />
        </div>
      </div>
    </div>
  );
}
